import axios from "axios";
import { BACKEND } from "../config/configEnvs";

const mailApi = axios.create({
  baseURL: `${BACKEND}/api/mail`,
  withCredentials: true,
});

let reconnectingAccounts = new Set();

const withAutoReconnect = async (requestFn, mailAccountId) => {
  try {
    return await requestFn();
  } catch (error) {
    if (error.response?.status === 503 && mailAccountId && !reconnectingAccounts.has(mailAccountId)) {
      reconnectingAccounts.add(mailAccountId);
      try {
        await mailApi.post(`/connect/${mailAccountId}`);
        return await requestFn();
      } finally {
        reconnectingAccounts.delete(mailAccountId);
      }
    }
    throw error;
  }
};

export const getAccounts = () => mailApi.get("/accounts");

export const connectAccount = (mailAccountId) =>
  mailApi.post(`/connect/${mailAccountId}`);

export const disconnectAccount = (mailAccountId) =>
  mailApi.post(`/disconnect/${mailAccountId}`);

export const getInbox = (mailAccountId, page = 1, limit = 20) =>
  withAutoReconnect(
    () => mailApi.get(`/inbox/${mailAccountId}`, { params: { page, limit } }),
    mailAccountId
  );

export const getMessage = (mailAccountId, uid, folder = "INBOX") =>
  withAutoReconnect(
    () => mailApi.get(`/message/${mailAccountId}/${uid}`, { params: { folder } }),
    mailAccountId
  );

/**
 * Descarga un adjunto. Devuelve el Blob para descargar o visualizar.
 * msg.attachments = [{ part, filename, contentType, size }]
 */
export const getAttachmentBlob = (mailAccountId, uid, part, folder = "INBOX") =>
  withAutoReconnect(
    () =>
      mailApi.get(`/attachment/${mailAccountId}/${uid}/${part}`, {
        params: { folder },
        responseType: "blob",
      }),
    mailAccountId
  ).then((res) => res.data);

export const updateMessageSeen = (mailAccountId, uid, seen, folder = "INBOX") =>
  withAutoReconnect(
    () =>
      mailApi.patch(`/message/${mailAccountId}/${uid}`, { seen }, { params: { folder } }),
    mailAccountId
  );

export const getFolders = (mailAccountId) =>
  withAutoReconnect(
    () => mailApi.get(`/folders/${mailAccountId}`),
    mailAccountId
  );

export const getFolderMessages = (mailAccountId, folder, page = 1, limit = 20) =>
  withAutoReconnect(
    () => mailApi.get(`/folder/${mailAccountId}/${encodeURIComponent(folder)}`, { params: { page, limit } }),
    mailAccountId
  );

export const searchMail = (mailAccountId, q, folder = "INBOX", limit = 50) =>
  withAutoReconnect(
    () => mailApi.get(`/search/${mailAccountId}`, { params: { q, folder, limit } }),
    mailAccountId
  );

export const getUnifiedInbox = (params = {}) => {
  const { mailAccountIds, folder = "INBOX", limit = 50, since, before, from } = params;
  const queryParams = { folder, limit };
  if (mailAccountIds?.length) queryParams.mailAccountIds = mailAccountIds.join(",");
  if (since) queryParams.since = since;
  if (before) queryParams.before = before;
  if (from) queryParams.from = from;
  return mailApi.get("/unified-inbox", { params: queryParams });
};

const MAX_ATTACHMENTS = 10;
const MAX_FILE_SIZE_MB = 25;

/**
 * Siempre multipart/form-data. No enviar Content-Type manual (axios lo genera solo con el boundary).
 * Adjuntos opcionales en "attachments" (máx 10 archivos, 25 MB c/u).
 */
export const sendMessage = ({ mailAccountId, to, cc, bcc, subject, text, html, attachments = [] }) =>
  withAutoReconnect(
    () => {
      const formData = new FormData();
      formData.append("mailAccountId", mailAccountId);
      formData.append("to", Array.isArray(to) ? to.join(",") : to ?? "");
      const ccStr = cc != null ? (Array.isArray(cc) ? cc.join(",") : String(cc)).trim() : "";
      if (ccStr) formData.append("cc", ccStr);
      const bccStr = bcc != null ? (Array.isArray(bcc) ? bcc.join(",") : String(bcc)).trim() : "";
      if (bccStr) formData.append("bcc", bccStr);
      formData.append("subject", subject || "(Sin asunto)");
      if (text) formData.append("text", text);
      if (html) formData.append("html", html);
      if (attachments?.length) {
        attachments.forEach((file) => formData.append("attachments", file));
      }
      return mailApi.post("/send", formData);
    },
    mailAccountId
  );

export { MAX_ATTACHMENTS, MAX_FILE_SIZE_MB };

export const deleteMessage = (mailAccountId, uid, folder = "INBOX") =>
  withAutoReconnect(
    () => mailApi.delete(`/message/${mailAccountId}/${uid}`, { params: { folder } }),
    mailAccountId
  );

export const moveMessage = (mailAccountId, uid, folder = "INBOX", destination) =>
  withAutoReconnect(
    () =>
      mailApi.patch(`/message/${mailAccountId}/${uid}/move`, { destination }, { params: { folder } }),
    mailAccountId
  );

export const getTrabajadores = () =>
  axios.get(`${BACKEND}/api/trabajadores?limit=0&depth=1`, { withCredentials: true });

export const getMailAccounts = () =>
  axios.get(`${BACKEND}/api/mail-accounts?limit=0&depth=1`, { withCredentials: true });

export const getMailAccount = (accountId) =>
  axios.get(`${BACKEND}/api/mail-accounts/${accountId}`, { withCredentials: true });

export const getTrabajador = (trabajadorId) =>
  axios.get(`${BACKEND}/api/trabajadores/${trabajadorId}`, { withCredentials: true });

export const updateMailAccountAccess = (accountId, trabajadoresConAcceso) =>
  axios.put(
    `${BACKEND}/api/mail-accounts/${accountId}`,
    { trabajadoresConAcceso },
    { withCredentials: true }
  );

export const updateMailAccountFirma = (accountId, firma) =>
  axios.patch(
    `${BACKEND}/api/mail-accounts/${accountId}`,
    { firma },
    { withCredentials: true }
  );

export const updateTrabajadorFirmaCorreo = (trabajadorId, firmaCorreo) =>
  axios.patch(
    `${BACKEND}/api/trabajadores/${trabajadorId}`,
    { firmaCorreo },
    { withCredentials: true }
  );

export const createMailAccount = (data) =>
  axios.post(`${BACKEND}/api/mail-accounts`, data, { withCredentials: true });

export const updateMailAccount = (accountId, data) =>
  axios.patch(`${BACKEND}/api/mail-accounts/${accountId}`, data, { withCredentials: true });

export const deleteMailAccount = (accountId) =>
  axios.delete(`${BACKEND}/api/mail-accounts/${accountId}`, { withCredentials: true });
